import { Link } from 'react-router-dom';
import styles from './Landing.module.css';

// The default-route landing page. Deliberately factual and semantic: one
// clear H1, a plain description of what the tool does, crawlable links into
// the three real views, feature documentation grouped by the tab it lives in
// (Analysis / Screener / Market Health), and an honest "not financial advice"
// note. The structure (single main, one H1, sectioned content with headings,
// real definition lists of capabilities) is what makes it both SEO- and
// LLM-extraction-friendly — no marketing superlatives, no keyword stuffing.
// Feature copy is kept in sync with the product reference in the sibling
// funviz-amplify repo's product.md (and, for Market Health specifically,
// docs/specs/market-health-spec.md until product.md catches up).
//
// Visual language: each tab section is styled as its own white card on the
// app's usual #f0f2f5 page background — the same idiom Screener and the
// Analysis ratio/valuation cards already use — rather than inventing a new
// look. The only accent colors are ones the app already assigns meaning to:
// the existing CTA blue (#4a90e2) for the two chart-analysis tools, and the
// Market Health "healthy" green (#28a745) for the Market Health tab, since
// that tab already owns a red/yellow/green vocabulary. The three nav CTAs
// carry small inline glyphs that echo each tab's own on-screen visual (a
// two-line chart, a filter funnel, and the same ring-arc technique
// ScoreGauge.js draws) — a wayfinding cue, not decoration.

const ACCENT = {
  analysis: '#4a90e2',
  screener: '#4a90e2',
  marketHealth: '#28a745',
};

// Echoes the main chart's "market value vs. fair value" pair of lines.
const AnalysisGlyph = () => (
  <svg viewBox="0 0 32 32" width="28" height="28" aria-hidden="true" focusable="false">
    <path
      d="M3 23 L11 14 L17 19 L29 5"
      fill="none"
      stroke="currentColor"
      strokeWidth="2.25"
      strokeLinecap="round"
      strokeLinejoin="round"
    />
    <path
      d="M3 19 L11 22 L17 11 L29 14"
      fill="none"
      stroke="currentColor"
      strokeWidth="2"
      strokeLinecap="round"
      strokeLinejoin="round"
      strokeDasharray="1 4"
      opacity="0.6"
    />
  </svg>
);

// A plain funnel — the screener narrows a universe of stocks down to a list.
const ScreenerGlyph = () => (
  <svg viewBox="0 0 32 32" width="28" height="28" aria-hidden="true" focusable="false">
    <path
      d="M4 5 H28 L18.5 17 V26 L13.5 23.5 V17 Z"
      fill="none"
      stroke="currentColor"
      strokeWidth="2.25"
      strokeLinejoin="round"
    />
  </svg>
);

// The same ring-arc construction ScoreGauge.js draws (full track + a
// dash-offset arc rotated -90deg) at a fixed ~70% fill, so this glyph is
// literally a small preview of the real gauge rather than a generic icon.
const MarketHealthGlyph = () => {
  const r = 11;
  const c = 2 * Math.PI * r;
  return (
    <svg viewBox="0 0 28 28" width="28" height="28" aria-hidden="true" focusable="false">
      <circle cx="14" cy="14" r={r} fill="none" stroke="#e9ecef" strokeWidth="3.5" />
      <circle
        cx="14"
        cy="14"
        r={r}
        fill="none"
        stroke="currentColor"
        strokeWidth="3.5"
        strokeLinecap="round"
        strokeDasharray={c}
        strokeDashoffset={c * 0.3}
        transform="rotate(-90 14 14)"
      />
    </svg>
  );
};

const Landing = () => (
  <main className={styles.landing}>
    <article className={styles.content}>
      <header className={styles.header}>
        <h1 className={styles.heading}>FunViz: Fundamentals Visualizer</h1>
        <p className={styles.lede}>
          See at a glance whether a stock is cheap or expensive relative to its
          fundamentals. FunViz is a free tool for long-term investors, with no
          sign-up and no paywall.
        </p>
        <div className={styles.headingAccent} aria-hidden="true" />
      </header>

      <div className={styles.launch}>
        <Link to="/analysis" className={styles.launchCta}>
          <span className={styles.launchIcon} style={{ color: '#fff' }}>
            <AnalysisGlyph />
          </span>
          Launch FunViz
        </Link>
        <p className={styles.launchNote}>
          Opens the analysis view instantly.
        </p>
      </div>

      <nav className={styles.ctaGrid} aria-label="Primary">
        <Link
          to="/analysis"
          className={styles.ctaCard}
          style={{ borderLeftColor: ACCENT.analysis }}
        >
          <span className={styles.ctaIcon} style={{ color: ACCENT.analysis }}>
            <AnalysisGlyph />
          </span>
          <span className={styles.ctaText}>
            <span className={styles.ctaLabel}>Open the analysis view</span>
            <span className={styles.ctaHint}>Chart any stock against fair value</span>
          </span>
        </Link>
        <Link
          to="/screener"
          className={styles.ctaCard}
          style={{ borderLeftColor: ACCENT.screener }}
        >
          <span className={styles.ctaIcon} style={{ color: ACCENT.screener }}>
            <ScreenerGlyph />
          </span>
          <span className={styles.ctaText}>
            <span className={styles.ctaLabel}>Browse the screener</span>
            <span className={styles.ctaHint}>Filter US stocks by fundamentals</span>
          </span>
        </Link>
        <Link
          to="/markethealth"
          className={styles.ctaCard}
          style={{ borderLeftColor: ACCENT.marketHealth }}
        >
          <span className={styles.ctaIcon} style={{ color: ACCENT.marketHealth }}>
            <MarketHealthGlyph />
          </span>
          <span className={styles.ctaText}>
            <span className={styles.ctaLabel}>Check market health</span>
            <span className={styles.ctaHint}>Today&rsquo;s market-conditions score</span>
          </span>
        </Link>
      </nav>

      <section
        className={styles.section}
        aria-labelledby="analysis-heading"
        style={{ borderLeftColor: ACCENT.analysis }}
      >
        <h2 id="analysis-heading" className={styles.sectionHeading}>
          Analysis tab
        </h2>
        <p>
          Look up any publicly traded stock and study three chart surfaces
          built around one question: is the price expensive or cheap relative
          to the fundamentals? The analysis view also charts a quality score,
          Rule of 40, and the margins and growth rates behind them.
        </p>
        <dl className={styles.features}>
          <div className={styles.feature}>
            <dt>Market value vs. fair value</dt>
            <dd>
              Overlay share price, market cap, or enterprise value against a
              fair-value line you control via a valuation multiple and your
              choice of eight fundamental metrics.
            </dd>
          </div>
          <div className={styles.feature}>
            <dt>Eight fundamentals metrics</dt>
            <dd>
              EPS, revenue, EBITDA, EBIT, net income, free cash flow, operating
              cash flow, or dividends, each as one continuous series from
              annual history through a forward estimate.
            </dd>
          </div>
          <div className={styles.feature}>
            <dt>Honest forward estimates</dt>
            <dd>
              Forward points are labelled by source (analyst consensus,
              implied cash-flow, or none) and visually separated from
              reported history.
            </dd>
          </div>
          <div className={styles.feature}>
            <dt>Ratio / yield over time</dt>
            <dd>
              See whether today&rsquo;s valuation multiple is high or low
              against the company&rsquo;s own history.
            </dd>
          </div>
          <div className={styles.feature}>
            <dt>Valuation vs. subsequent returns</dt>
            <dd>
              A per-company backtest: has buying cheap historically led to
              higher returns for this specific stock?
            </dd>
          </div>
          <div className={styles.feature}>
            <dt>Full control over the view</dt>
            <dd>
              Switch between market cap and enterprise value, per-share and
              whole-company, with automatic currency conversion when needed.
            </dd>
          </div>
        </dl>
      </section>

      <section
        className={styles.section}
        aria-labelledby="screener-heading"
        style={{ borderLeftColor: ACCENT.screener }}
      >
        <h2 id="screener-heading" className={styles.sectionHeading}>
          Screener tab
        </h2>
        <p>
          Scan US stocks by fundamentals instead of looking them up one at a
          time.
        </p>
        <dl className={styles.features}>
          <div className={styles.feature}>
            <dt>Filter by fundamentals</dt>
            <dd>
              Narrow by dividend yield, earnings stability, payoff years,
              volume, and sector.
            </dd>
          </div>
          <div className={styles.feature}>
            <dt>Sortable, paginated results</dt>
            <dd>
              Sort by any column and page through the matches.
            </dd>
          </div>
          <div className={styles.feature}>
            <dt>One click into analysis</dt>
            <dd>
              Open any row in the full analysis view without losing your
              screener filters.
            </dd>
          </div>
        </dl>
        <p className={styles.calloutNote}>
          The screener covers US common stocks only. The per-stock analysis view
          works for international tickers too.
        </p>
      </section>

      <section
        className={styles.section}
        aria-labelledby="markethealth-heading"
        style={{ borderLeftColor: ACCENT.marketHealth }}
      >
        <h2 id="markethealth-heading" className={styles.sectionHeading}>
          Market Health tab
        </h2>
        <p>
          A daily read of overall US market conditions, independent of
          any one stock, shown as a color-coded gauge you can check
          in seconds.
        </p>
        <dl className={styles.features}>
          <div className={styles.feature}>
            <dt>Four investment strategies</dt>
            <dd>
              Adaptive, Balanced, Yield Curve, and Conservative, each
              with its own risk profile, trade frequency, and daily score.
            </dd>
          </div>
          <div className={styles.feature}>
            <dt>Per-strategy gauge and history</dt>
            <dd>
              Each strategy gets a color-coded gauge and a score-over-time chart
              with red/yellow/green zones.
            </dd>
          </div>
          <div className={styles.feature}>
            <dt>S&amp;P 500 drawdown-protection backtest</dt>
            <dd>
              Compare buy-and-hold drawdowns against each strategy&rsquo;s
              equity curve over 33&nbsp;years of history.
            </dd>
          </div>
          <div className={styles.feature}>
            <dt>Execution-lag toggle</dt>
            <dd>
              See results with realistic next-day execution or with same-day
              signals.
            </dd>
          </div>
        </dl>
        <p className={styles.calloutNote}>
          A healthy score means broadly favorable conditions, not a buy
          recommendation. Extreme lows have historically preceded
          recoveries.
        </p>
      </section>

      <section className={styles.aboutSection} aria-labelledby="about-heading">
        <h2 id="about-heading" className={styles.aboutHeading}>About the data</h2>
        <p>
          Price and financial data come from a third-party market-data provider.
          FunViz is a visualization tool, not financial advice. It does not
          make recommendations or predict future prices.
        </p>
      </section>
    </article>
  </main>
);

export default Landing;
